import React, { useCallback, useMemo, useRef, useState } from "react";
import { Box, Button, Chip, CircularProgress, Typography } from "@mui/material";
import {
  Circle,
  GoogleMap,
  InfoWindow,
  Marker,
  useLoadScript,
} from "@react-google-maps/api";

// ─── Map constants ─────────────────────────────────────────────────────────────
const MAP_CONTAINER_STYLE = { width: "100%", height: "500px" };
const INDIA_CENTER = { lat: 20.5937, lng: 78.9629 };
const LIBRARIES = ["places"];

const PIN_PATH =
  "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" +
  "m0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z";

const MARKER_CONFIG = {
  DC_HUB: { fillColor: "#1565C0", scale: 2.4, label: "Delivery Center" },
  // LINKED_CITY: { fillColor: "#2E7D32", scale: 1.8, label: "Linked City" },
  MAPPED_STORE: { fillColor: "#F57C00", scale: 1.8, label: "Mapped Store" },
  UNMAPPED: { fillColor: "#C62828", scale: 1.8, label: "Unmapped Store" },
  // Visual variant for a lasso-selected unmapped pin
  UNMAPPED_SELECTED: { fillColor: "#7B1FA2", scale: 2.0, label: "Selected" },
};

const makeIcon = (type) => {
  const cfg = MARKER_CONFIG[type] ?? MARKER_CONFIG.UNMAPPED;
  return {
    path: PIN_PATH,
    fillColor: cfg.fillColor,
    fillOpacity: 1,
    strokeColor: "#FFFFFF",
    strokeWeight: type === "UNMAPPED_SELECTED" ? 2.5 : 1.5,
    scale: cfg.scale,
    anchor: new window.google.maps.Point(12, 24),
  };
};

const toLatLng = (coords) => {
  if (!coords) return null;
  const lat = parseFloat(coords.lat ?? coords.latitude);
  const lng = parseFloat(coords.lng ?? coords.longitude);
  if (isNaN(lat) || isNaN(lng) || (lat === 0 && lng === 0)) return null;
  return { lat, lng };
};

// ─── Map legend ────────────────────────────────────────────────────────────────
const MapLegend = ({ distance, showRadius, lassoActive, lassoCount }) => (
  <Box
    sx={{
      position: "absolute",
      bottom: 32,
      left: 12,
      bgcolor: "rgba(255,255,255,0.95)",
      borderRadius: 2,
      px: 2,
      py: 1.5,
      boxShadow: 3,
      zIndex: 10,
      display: "flex",
      flexDirection: "column",
      gap: 0.8,
      minWidth: 170,
    }}
  >
    <Typography variant="caption" fontWeight={700} mb={0.5}>
      Map Legend
    </Typography>
    {Object.entries(MARKER_CONFIG)
      .filter(([key]) => key !== "UNMAPPED_SELECTED")
      .map(([, cfg]) => (
        <Box key={cfg.label} display="flex" alignItems="center" gap={1}>
          <Box
            sx={{
              width: 13,
              height: 13,
              borderRadius: "50%",
              bgcolor: cfg.fillColor,
              border: "1.5px solid #fff",
              boxShadow: `0 0 0 1px ${cfg.fillColor}`,
              flexShrink: 0,
            }}
          />
          <Typography variant="caption">{cfg.label}</Typography>
        </Box>
      ))}
    {showRadius && (
      <Box display="flex" alignItems="center" gap={1} mt={0.5}>
        <Box
          sx={{
            width: 13,
            height: 13,
            borderRadius: "50%",
            border: "2px solid #1565C0",
            bgcolor: "rgba(21,101,192,0.12)",
            flexShrink: 0,
          }}
        />
        <Typography variant="caption">
          Radius <strong>{distance} km</strong>
        </Typography>
      </Box>
    )}
    <Box mt={0.5} pt={0.5} borderTop="1px solid #eee">
      <Typography
        variant="caption"
        color={lassoActive ? "secondary" : "text.disabled"}
        fontWeight={lassoActive ? 700 : 400}
      >
        {lassoActive
          ? lassoCount > 0
            ? `⬜ ${lassoCount} store${lassoCount > 1 ? "s" : ""} selected`
            : "⬜ Drag to select stores"
          : "Hold Shift + drag to lasso select"}
      </Typography>
    </Box>
  </Box>
);

// ─── Lasso selection action bar ────────────────────────────────────────────────
// Floats above the map when stores are lasso-selected.
const LassoActionBar = ({ count, mappedStores, onAssign, onClear }) => {
  if (count === 0) return null;
  return (
    <Box
      sx={{
        position: "absolute",
        top: 12,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 20,
        bgcolor: "#fff",
        borderRadius: 3,
        boxShadow: 4,
        px: 2,
        py: 1,
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        flexWrap: "wrap",
      }}
    >
      <Chip
        label={`${count} store${count > 1 ? "s" : ""} selected`}
        color="secondary"
        size="small"
        sx={{ fontWeight: 700 }}
      />
      <Typography variant="caption" color="text.secondary">
        Assign to:
      </Typography>
      {mappedStores.map((dc) => (
        <Button
          key={dc._id}
          size="small"
          variant="contained"
          onClick={() => onAssign(dc._id)}
          sx={{ whiteSpace: "nowrap", textTransform: "none" }}
        >
          {dc.cityName} DC
        </Button>
      ))}
      <Button size="small" color="inherit" onClick={onClear}>
        Cancel
      </Button>
    </Box>
  );
};

// ─── Main component ────────────────────────────────────────────────────────────
const MapDisplayForStores = ({
  selectedDC,
  unmappedStores = [],
  geocodeProgress,
  selectedMarker,
  setSelectedMarker,
  setSelectedDCId,
  selectedDCId,
  onMoveToMapped,
  mappedStores = [], // all DC batches — shown as assign targets in lasso bar
  onBulkMoveToMapped, // (stores[], dcId) => void
}) => {
  const mapRef = useRef(null); // holds the google.maps.Map instance
  const overlayRef = useRef(null); // the transparent drag-capture div

  // ── Lasso state ────────────────────────────────────────────────────────────
  const [lassoActive, setLassoActive] = useState(false); // Shift is held
  const [dragging, setDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null); // { x, y } in px
  const [dragCurrent, setDragCurrent] = useState(null); // { x, y } in px
  const [lassoSelectedStores, setLassoSelectedStores] = useState([]); // store objects

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAP_API_KEY,
    libraries: LIBRARIES,
  });

  // ── Build map markers ─────────────────────────────────────────────────────
  // Lasso-selected stores render as UNMAPPED_SELECTED (purple) instead of red.
  const lassoSelectedSet = useMemo(
    () => new Set(lassoSelectedStores),
    [lassoSelectedStores],
  );

  const mapMarkers = useMemo(() => {
    const markers = [];

    unmappedStores.forEach((store) => {
      const coords = toLatLng(store.locationCoordinates);
      if (!coords) return;
      markers.push({
        store, // keep reference for lasso lookup
        type: lassoSelectedSet.has(store) ? "UNMAPPED_SELECTED" : "UNMAPPED",
        lat: coords.lat,
        lng: coords.lng,
        name: store.details?.storeName || "Unmapped Store",
        storeOwnerPhoneNumber: store.details?.storeOwnerPhoneNumber || "",
        extra: [store.details?.city, store.details?.state]
          .filter(Boolean)
          .join(", "),
      });
    });

    if (!selectedDC) return markers;

    const dcCoords = toLatLng(selectedDC.coordinates);
    if (dcCoords)
      markers.push({
        type: "DC_HUB",
        lat: dcCoords.lat,
        lng: dcCoords.lng,
        name: `${selectedDC.cityName} Delivery Center`,
        extra: selectedDC.stateName || "",
      });

    // (selectedDC.linkedCities || []).forEach((link) => {
    //   const coords = toLatLng(link.coordinates);
    //   if (!coords) return;
    //   markers.push({
    //     type: "LINKED_CITY",
    //     lat: coords.lat,
    //     lng: coords.lng,
    //     name: link.cityName,
    //     extra: selectedDC.stateName || "",
    //   });
    // });

    (selectedDC.stores || []).forEach((store) => {
      const coords = toLatLng(store.locationCoordinates);
      if (!coords) return;
      markers.push({
        type: "MAPPED_STORE",
        lat: coords.lat,
        lng: coords.lng,
        name: store.details?.storeName || "Store",
        extra: [store.details?.city, store.details?.state]
          .filter(Boolean)
          .join(", "),
      });
    });

    return markers;
  }, [selectedDC, unmappedStores, lassoSelectedSet]);

  const mapCenter = useMemo(() => {
    if (selectedDC) {
      const coords = toLatLng(selectedDC.coordinates);
      if (coords) return coords;
    }
    if (mapMarkers.length === 0) return INDIA_CENTER;
    const lats = mapMarkers.map((m) => m.lat);
    const lngs = mapMarkers.map((m) => m.lng);
    return {
      lat: (Math.min(...lats) + Math.max(...lats)) / 2,
      lng: (Math.min(...lngs) + Math.max(...lngs)) / 2,
    };
  }, [mapMarkers]);

  // Per-DC radius: read live from selectedDC.radius (updated by the accordion slider)
  const distance = selectedDC?.radius ?? 10;

  const dcCircle = useMemo(() => {
    if (!selectedDC) return null;
    return toLatLng(selectedDC.coordinates);
  }, [selectedDC]);

  // ── Single-pin assign (InfoWindow button) ─────────────────────────────────
  const handleMoveSelectedMarkerToDC = (marker, dcId) => {
    if (!marker) return;
    const store = unmappedStores.find(
      (s) =>
        s?.details?.storeOwnerPhoneNumber === marker.storeOwnerPhoneNumber &&
        s?.details?.storeName === marker.name,
    );
    if (store) onMoveToMapped(store, dcId);
  };

  // ── Lasso: convert pixel rect → lat/lng bounds, collect stores inside ─────
  /**
   * Given two pixel corners of the selection box, convert them to geographic
   * bounds using the map's projection, then test each unmapped store marker.
   *
   * google.maps.Map.getProjection() → MapCanvasProjection isn't available on
   * the raw map instance. Instead we use map.getBounds() + fromLatLngToPoint()
   * via an OverlayView trick — but the simplest approach that works in
   * @react-google-maps/api is to use the undocumented but stable
   * map.getProjection().fromPointToLatLng() after converting px → world point.
   */
  const computeLassoSelection = useCallback(
    (start, end) => {
      const map = mapRef.current;
      if (!map || !map.getProjection()) return;

      const bounds = map.getBounds();
      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();
      const mapDiv = map.getDiv();
      const mapW = mapDiv.offsetWidth;
      const mapH = mapDiv.offsetHeight;

      // Convert a pixel position (relative to the map div) → { lat, lng }
      const pixelToLatLng = (px, py) => {
        const lat = ne.lat() - (py / mapH) * (ne.lat() - sw.lat());
        const lng = sw.lng() + (px / mapW) * (ne.lng() - sw.lng());
        return { lat, lng };
      };

      const minX = Math.min(start.x, end.x);
      const maxX = Math.max(start.x, end.x);
      const minY = Math.min(start.y, end.y);
      const maxY = Math.max(start.y, end.y);

      const swBound = pixelToLatLng(minX, maxY); // bottom-left
      const neBound = pixelToLatLng(maxX, minY); // top-right

      const selected = unmappedStores.filter((store) => {
        const coords = toLatLng(store.locationCoordinates);
        if (!coords) return false;
        return (
          coords.lat >= swBound.lat &&
          coords.lat <= neBound.lat &&
          coords.lng >= swBound.lng &&
          coords.lng <= neBound.lng
        );
      });

      setLassoSelectedStores(selected);
    },
    [unmappedStores],
  );

  // ── Keyboard listeners for Shift key ──────────────────────────────────────
  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Shift") setLassoActive(e.type === "keydown");
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("keyup", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("keyup", onKey);
    };
  }, []);

  // ── Mouse handlers on the overlay div ────────────────────────────────────
  const getRelativeXY = (e, el) => {
    const rect = el.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const handleMouseDown = useCallback(
    (e) => {
      if (!lassoActive) return;
      e.preventDefault();
      const pos = getRelativeXY(e, overlayRef.current);
      setDragging(true);
      setDragStart(pos);
      setDragCurrent(pos);
      setLassoSelectedStores([]);
      setSelectedMarker(null);
    },
    [lassoActive, setSelectedMarker],
  );

  const handleMouseMove = useCallback(
    (e) => {
      if (!dragging) return;
      e.preventDefault();
      const pos = getRelativeXY(e, overlayRef.current);
      setDragCurrent(pos);
    },
    [dragging],
  );

  const handleMouseUp = useCallback(
    (e) => {
      if (!dragging) return;
      e.preventDefault();
      const pos = getRelativeXY(e, overlayRef.current);
      setDragging(false);
      computeLassoSelection(dragStart, pos);
      setDragStart(null);
      setDragCurrent(null);
    },
    [dragging, dragStart, computeLassoSelection],
  );

  // ── Lasso assign ──────────────────────────────────────────────────────────
  const handleLassoAssign = useCallback(
    (dcId) => {
      if (onBulkMoveToMapped && lassoSelectedStores.length > 0) {
        onBulkMoveToMapped(lassoSelectedStores, dcId);
      }
      setLassoSelectedStores([]);
    },
    [lassoSelectedStores, onBulkMoveToMapped],
  );

  // ── Selection box geometry ────────────────────────────────────────────────
  const selectionBox = useMemo(() => {
    if (!dragging || !dragStart || !dragCurrent) return null;
    return {
      left: Math.min(dragStart.x, dragCurrent.x),
      top: Math.min(dragStart.y, dragCurrent.y),
      width: Math.abs(dragCurrent.x - dragStart.x),
      height: Math.abs(dragCurrent.y - dragStart.y),
    };
  }, [dragging, dragStart, dragCurrent]);

  if (!isLoaded)
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height={500}
        gap={1}
      >
        <CircularProgress size={24} />
        <Typography variant="body2" color="text.secondary">
          Loading map…
        </Typography>
      </Box>
    );

  return (
    <Box>
      {/* ── Status bar ─────────────────────────────────────────────────────── */}
      <Box
        sx={{
          mb: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="body2" color="text.secondary" fontStyle="italic">
          {lassoSelectedStores.length > 0
            ? `${lassoSelectedStores.length} store${lassoSelectedStores.length > 1 ? "s" : ""} selected — assign to a DC using the bar above the map`
            : selectedDC
              ? `Showing: ${selectedDC.cityName} DC · ${selectedDC.stores.length} mapped stores · ${selectedDC.linkedCities?.length ?? 0} linked cities`
              : geocodeProgress !== null
                ? "Locating stores — pins appear as coordinates resolve…"
                : `${mapMarkers.length} pins visible · hold Shift + drag to lasso-select unmapped stores`}
        </Typography>
        {selectedDC && lassoSelectedStores.length === 0 && (
          <Button size="small" onClick={() => setSelectedDCId(null)}>
            Clear Focus
          </Button>
        )}
      </Box>

      {/* ── Map container ─────────────────────────────────────────────────── */}
      <Box sx={{ position: "relative", borderRadius: 2, overflow: "hidden" }}>
        {/* Lasso assign action bar — floats over the map */}
        <LassoActionBar
          count={lassoSelectedStores.length}
          mappedStores={mappedStores}
          onAssign={handleLassoAssign}
          onClear={() => setLassoSelectedStores([])}
        />

        <GoogleMap
          mapContainerStyle={MAP_CONTAINER_STYLE}
          zoom={mapMarkers.length > 0 ? 11 : 5}
          center={mapCenter}
          options={{
            mapTypeControl: false,
            streetViewControl: false,
            // Disable Google's built-in drag while lasso is active so the map
            // doesn't pan when the user draws the selection rectangle.
            draggable: !lassoActive,
            gestureHandling: lassoActive ? "none" : "auto",
          }}
          onLoad={(map) => {
            mapRef.current = map;
          }}
          onClick={() => {
            if (!lassoActive) setSelectedMarker(null);
          }}
        >
          {mapMarkers.map((m, i) =>
            m.type === "DC_HUB" ? (
              <Marker
                key={`${m.type}-${i}`}
                position={{ lat: m.lat, lng: m.lng }}
                title={m.name}
                icon={{
                  url:
                    "data:image/svg+xml;charset=UTF-8," +
                    encodeURIComponent(`
            <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 32 33"
      fill="none"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M25.9999 26.7299H27.9999C28.4242 26.7299 28.8312 26.8984 29.1313 27.1985C29.4313 27.4986 29.5999 27.9055 29.5999 28.3299C29.5999 28.7542 29.4313 29.1612 29.1313 29.4612C28.8312 29.7613 28.4242 29.9299 27.9999 29.9299H3.9999C3.57556 29.9299 3.16859 29.7613 2.86853 29.4612C2.56847 29.1612 2.3999 28.7542 2.3999 28.3299C2.3999 27.9055 2.56847 27.4986 2.86853 27.1985C3.16859 26.8984 3.57556 26.7299 3.9999 26.7299H5.9999V15.7149C7.30535 15.8906 8.62749 15.545 9.6799 14.7529L11.0239 13.7339C11.1457 13.6449 11.2926 13.5969 11.4434 13.5969C11.5942 13.5969 11.7411 13.6449 11.8629 13.7339L13.0089 14.6039C13.8785 15.2567 14.9365 15.6096 16.0239 15.6096C17.1113 15.6096 18.1693 15.2567 19.0389 14.6039L20.1859 13.7339C20.3077 13.6449 20.4546 13.5969 20.6054 13.5969C20.7562 13.5969 20.9031 13.6449 21.0249 13.7339L22.3689 14.7539C23.4075 15.5352 24.7093 15.882 25.9989 15.7209L25.9999 26.7299ZM11.5999 26.7299H13.9999V22.5599C13.9999 22.0294 14.2106 21.5207 14.5857 21.1457C14.9608 20.7706 15.4695 20.5599 15.9999 20.5599C16.5303 20.5599 17.039 20.7706 17.4141 21.1457C17.7892 21.5207 17.9999 22.0294 17.9999 22.5599V26.7299H20.3999V22.5599C20.3999 21.3929 19.9363 20.2738 19.1112 19.4486C18.286 18.6234 17.1669 18.1599 15.9999 18.1599C14.833 18.1599 13.7138 18.6234 12.8886 19.4486C12.0635 20.2738 11.5999 21.3929 11.5999 22.5599V26.7299Z"
        fill="#181818"
      />
      <path
        d="M4.53979 5.24786C4.59979 5.08786 4.66579 4.92886 4.76679 4.78786C5.39379 3.90586 6.51379 3.35986 7.73079 3.35986H9.59979V12.1039L9.30279 12.3069C8.12279 13.1169 6.46679 13.1789 5.20879 12.4599L4.66379 12.1479C2.29279 10.7909 3.31779 8.27486 4.16479 6.19686C4.29879 5.86686 4.42879 5.54986 4.53979 5.24786ZM11.9998 11.1359V3.35986H19.9998V11.1359C19.7329 11.1799 19.4788 11.2812 19.2548 11.4329L18.1688 12.1789C16.9008 13.0489 15.0988 13.0489 13.8308 12.1789L12.7448 11.4329C12.5208 11.2812 12.2667 11.1799 11.9998 11.1359ZM22.3998 12.1039L22.6968 12.3069C23.8768 13.1169 25.5328 13.1789 26.7908 12.4599L27.3358 12.1479C29.7068 10.7909 28.6818 8.27486 27.8348 6.19686C27.7008 5.86686 27.5708 5.54886 27.4598 5.24686C27.4048 5.08415 27.3285 4.92946 27.2328 4.78686C26.6058 3.90686 25.4858 3.35986 24.2688 3.35986H22.3988L22.3998 12.1039Z"
        fill="#181818"
      />
    </svg>
          `),
                  scaledSize: new window.google.maps.Size(36, 36),
                }}
                zIndex={1000}
                onClick={() => {
                  if (!lassoActive) setSelectedMarker(m);
                }}
              />
            ) : (
              <Marker
                key={`${m.type}-${i}`}
                position={{ lat: m.lat, lng: m.lng }}
                icon={makeIcon(m.type)}
                title={m.name}
                zIndex={
                  m.type === "UNMAPPED_SELECTED"
                    ? 80
                    : m.type === "MAPPED_STORE"
                      ? 50
                      : m.type === "LINKED_CITY"
                        ? 30
                        : 10
                }
                onClick={() => {
                  if (!lassoActive) setSelectedMarker(m);
                }}
              />
            ),
          )}

          {dcCircle && (
            <Circle
              center={dcCircle}
              radius={Number(distance) * 1000}
              options={{
                strokeColor: "#1565C0",
                strokeOpacity: 0.9,
                strokeWeight: 2,
                fillColor: "#1565C0",
                fillOpacity: 0.08,
                zIndex: 1,
              }}
            />
          )}

          {selectedMarker && !lassoActive && (
            <InfoWindow
              position={{ lat: selectedMarker.lat, lng: selectedMarker.lng }}
              onCloseClick={() => setSelectedMarker(null)}
            >
              <Box sx={{ p: 0.5, minWidth: 160 }}>
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  sx={{
                    color: (
                      MARKER_CONFIG[selectedMarker.type] ??
                      MARKER_CONFIG.UNMAPPED
                    ).fillColor,
                  }}
                >
                  {
                    {
                      DC_HUB: "🏭 Delivery Center",
                      LINKED_CITY: "🏙️ Linked City",
                      MAPPED_STORE: "🏪 Mapped Store",
                      UNMAPPED: "📍 Unmapped Store",
                      UNMAPPED_SELECTED: "📍 Selected Store",
                    }[selectedMarker.type]
                  }
                </Typography>
                <Typography variant="body2" fontWeight={600} mt={0.5}>
                  {selectedMarker.name}
                </Typography>
                {selectedMarker.extra && (
                  <Typography variant="caption" color="text.secondary">
                    {selectedMarker.extra}
                  </Typography>
                )}
                <Typography
                  variant="caption"
                  color="text.disabled"
                  display="block"
                  mt={0.5}
                >
                  {selectedMarker.lat.toFixed(4)},{" "}
                  {selectedMarker.lng.toFixed(4)}
                </Typography>
                {selectedMarker.type === "UNMAPPED" && selectedDCId && (
                  <Button
                    size="small"
                    onClick={() =>
                      handleMoveSelectedMarkerToDC(selectedMarker, selectedDCId)
                    }
                  >
                    {`Assign to ${selectedDC?.cityName} DC`}
                  </Button>
                )}
              </Box>
            </InfoWindow>
          )}
        </GoogleMap>

        {/* ── Transparent overlay that captures mouse events for lasso ──────
             Sits on top of the map. When Shift is NOT held it ignores all
             pointer events so normal map interaction still works.            */}
        <Box
          ref={overlayRef}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp} // cancel if mouse leaves the map
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 5,
            cursor: lassoActive ? "crosshair" : "default",
            pointerEvents: lassoActive ? "all" : "none", // transparent when not lassoing
          }}
        >
          {/* ── Rubber-band rectangle ────────────────────────────────────── */}
          {selectionBox && (
            <Box
              sx={{
                position: "absolute",
                left: selectionBox.left,
                top: selectionBox.top,
                width: selectionBox.width,
                height: selectionBox.height,
                border: "2px dashed #7B1FA2",
                bgcolor: "rgba(123,31,162,0.08)",
                borderRadius: 1,
                pointerEvents: "none",
              }}
            />
          )}
        </Box>

        <MapLegend
          distance={distance}
          showRadius={Boolean(dcCircle)}
          lassoActive={lassoActive}
          lassoCount={lassoSelectedStores.length}
        />
      </Box>
    </Box>
  );
};

export default MapDisplayForStores;
